import React from "react";
import { Input, Select } from "antd";
import countryCodes from "../../core/data/country-phone-codes.json";

/**
 * Çalışma alanının kendi form alanları.
 *
 * Eski ekranın bileşenleri (PhoneInput, CurrencySelect, …) bilerek kullanılmaz: onlar kendi
 * inline stillerini, kendi rota/context varsayımlarını taşıyor ve v2'nin görsel dilini bozuyor.
 * Buradakiler yalnızca antd'nin ham girdi bileşenlerine dayanır.
 */

export const CURRENCY_SYMBOL = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };
const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];

/** Para birimi seçici. */
export function CurrencyField({ value, onChange, disabled, size = 'small', style }) {
    return (
        <Select
            size={size}
            disabled={disabled}
            value={value || 'TRY'}
            onChange={onChange}
            style={{ width: 96, ...style }}
            options={CURRENCIES.map(c => ({ value: c, label: `${c} ${CURRENCY_SYMBOL[c]}` }))}
        />
    );
}

/** Telefon biçimlendirmesi: (5XX) XXX XX XX — en fazla 10 hane. */
export function formatPhone(raw) {
    const digits = (raw || '').replace(/\D+/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 8) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
}

export function stripPhone(formatted) {
    return (formatted || '').replace(/\D+/g, '');
}

const phoneOptions = countryCodes.map(c => ({
    value: c.code,
    label: `${c.emoji} +${c.code}`,
    search: `${c.name} ${c.short} ${c.code}`.toLowerCase(),
}));

/**
 * Ülke kodu + telefon. Değer biçimi { code, phone } — sunucudaki phoneCode/phone ayrımıyla aynı.
 *
 * Silme davranışı: biçim karakterine (parantez, boşluk) denk gelen bir silme ham metni kısaltır
 * ama rakam sayısını değiştirmez; bu durumda bir rakam da eksiltilir, aksi halde alan kodu
 * hiçbir zaman silinemezdi.
 */
export function PhoneField({ value = {}, onChange, disabled, size = 'middle' }) {
    const emit = (next) => onChange?.({ code: value.code ?? 90, phone: value.phone ?? '', ...next });

    const handleChange = (e) => {
        const raw = e.target.value ?? '';
        const prev = value.phone ?? '';
        let digits = stripPhone(raw);
        if (raw.length < prev.length && digits === stripPhone(prev)) {
            digits = digits.slice(0, -1);
        }
        emit({ phone: formatPhone(digits) });
    };

    return (
        <Input
            size={size}
            className="ws-mono"
            disabled={disabled}
            value={value.phone ?? ''}
            onChange={handleChange}
            inputMode="numeric"
            type="tel"
            autoComplete="off"
            addonBefore={(
                <Select
                    showSearch
                    disabled={disabled}
                    value={value.code ?? 90}
                    onChange={(code) => emit({ code })}
                    options={phoneOptions}
                    optionFilterProp="search"
                    filterOption={(input, option) => option.search.includes(input.toLowerCase())}
                    style={{ width: 104 }}
                    popupMatchSelectWidth={280}
                />
            )}
        />
    );
}

/** <input type="datetime-local"> sarmalayıcısı — mobilde işletim sisteminin kendi seçicisini açar. */
export function DateTimeField({ value, onChange, disabled, readOnly }) {
    return (
        <Input
            type={readOnly ? 'text' : 'datetime-local'}
            className="ws-mono"
            disabled={disabled}
            readOnly={readOnly}
            value={value ?? ''}
            onChange={e => onChange?.(e.target.value)}
        />
    );
}

/** Para biçimlendirme — tabular hizalı, Türkçe ayraçlı. */
export function money(value, currency = 'TRY') {
    const v = Number(value) || 0;
    const sign = v < 0 ? '-' : '';
    const parts = Math.abs(v).toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${sign}${parts[0]},${parts[1]} ${CURRENCY_SYMBOL[currency] || currency}`;
}

/** Yetkisi olmayan kullanıcıya fiyat gösterilmez; sunucu da -1 döndürür. */
export function maskedMoney(value, currency, canView) {
    if (!canView || value === -1) return '•••';
    return money(value, currency);
}
